import { useCallback, useEffect, useRef, useState } from 'react';
import { EveningShowcaseStrip } from '../commercial/EveningShowcaseStrip';
import { SponsoredPlacementCard } from '../commercial/SponsoredPlacementCard';
import { fetchCityFeed, fetchFollowingFeed, SocialApiError, type ActivityEvent } from '../socialApi';
import { Button } from '../ui/Button';
import { DesktopThreeColumnLayout } from '../ui/DesktopThreeColumnLayout';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { Skeleton } from '../ui/Skeleton';
import { CityLeaderboardCard } from './CityLeaderboardCard';
import { FeedCard } from './FeedCard';
import { FeedContextSidebar } from './FeedContextSidebar';
import { PulseRoutesSoonScreen, PulseSavedPlacesScreen, PulseVisitsScreen } from './PulseAuxiliaryScreens';
import { PulseMobileNav } from './PulseMobileNav';
import { VenueRankingCard } from './VenueRankingCard';
import type { UserContextActions, UserContextData } from '../userContext/UserContext';
import './pulse-experience-v1.css';
import './pulse-offer-corrective.css';
import './pulse-mobile-header-order.css';
import './pulse-tz27-1.css';

type FeedScreenProps = {
  cityId: string;
  cityName: string;
  authenticated: boolean;
  userContext: UserContextData;
  userContextActions: UserContextActions;
  onOpenUser: (userId: string) => void;
  onOpenVenue: (venueId: string) => void;
  onShowVenueOnMap: (venueId: string) => void;
  onScan: () => void;
  cityStatePanel?: React.ReactNode;
};

type FeedTab = 'city' | 'following';
type PulseView = 'pulse' | 'visits' | 'saved' | 'routes';
type FeedSnapshot = {
  events: ActivityEvent[];
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasLoadedOnce: boolean;
  error: string | null;
  loadedAt: number;
};
const EMPTY_FEED: FeedSnapshot = {
  events: [], cursor: null, hasMore: false, loading: false, loadingMore: false,
  hasLoadedOnce: false, error: null, loadedAt: 0,
};
const FEED_REVALIDATE_MS = 60_000;

function FeedSkeleton() {
  return (
    <section className="pulse-feed-skeleton" aria-label="Загружаем ленту">
      {Array.from({ length: 3 }, (_, index) => (
        <div className="pulse-feed-skeleton__card" key={index} aria-hidden="true">
          <div className="pulse-feed-skeleton__row"><Skeleton variant="circle" width={28} height={28} /><Skeleton width="42%" height={14} /></div>
          <Skeleton width="72%" height={24} />
          <Skeleton width="100%" height={14} />
          <Skeleton width="64%" height={14} />
        </div>
      ))}
    </section>
  );
}

export function FeedScreen({
  cityId,
  cityName,
  authenticated,
  userContext,
  userContextActions,
  onOpenUser,
  onOpenVenue,
  onShowVenueOnMap,
  onScan,
  cityStatePanel,
}: FeedScreenProps) {
  const [view, setView] = useState<PulseView>('pulse');
  const [tab, setTab] = useState<FeedTab>('city');
  const [mobileRanking, setMobileRanking] = useState<'venues' | 'guests'>('venues');
  const [desktopRankings, setDesktopRankings] = useState(() => window.matchMedia('(min-width: 1120px)').matches);
  // Ephemeral tab-specific snapshots. Never persist personalized feed data in SW/storage.
  const [feeds, setFeeds] = useState<Record<string, FeedSnapshot>>({});
  const identity = authenticated ? userContext.userId || 'unknown' : 'anonymous';
  const scope = `${cityId}|${identity}`;
  const cacheKey = `${scope}|${tab}`;
  const snapshot = feeds[cacheKey] ?? EMPTY_FEED;
  const { events, cursor, hasMore, loading, loadingMore, hasLoadedOnce, error } = snapshot;
  const sequence = useRef(0);
  const currentKey = useRef(cacheKey);
  currentKey.current = cacheKey;
  const lastScope = useRef(scope);
  const cacheRef = useRef(feeds);
  cacheRef.current = feeds;

  const updateFeed = useCallback((key: string, update: (feed: FeedSnapshot) => FeedSnapshot) => {
    setFeeds((previous) => {
      const next = { ...previous, [key]: update(previous[key] ?? EMPTY_FEED) };
      cacheRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(min-width: 1120px)');
    const update = () => setDesktopRankings(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // Drop all snapshots on account/city change, including the old following feed.
  useEffect(() => {
    if (lastScope.current !== scope) {
      lastScope.current = scope;
      sequence.current += 1;
      cacheRef.current = {};
      setFeeds({});
    }
  }, [scope]);

  const load = useCallback(async (append: boolean) => {
    const key = cacheKey;
    const cached = cacheRef.current[key] ?? EMPTY_FEED;
    if ((tab === 'city' && !cityId) || (tab === 'following' && !authenticated)) {
      sequence.current += 1;
      updateFeed(key, () => ({ ...EMPTY_FEED, hasLoadedOnce: true }));
      return;
    }
    if (append && (cached.loadingMore || cached.loading || !cached.hasMore)) return;

    const id = ++sequence.current;
    updateFeed(key, (current) => ({
      ...current, loading: !append, loadingMore: append, error: null,
    }));
    try {
      const response = tab === 'city'
        ? await fetchCityFeed(cityId, append ? cached.cursor : null)
        : await fetchFollowingFeed(append ? cached.cursor : null);
      if (id !== sequence.current || currentKey.current !== key) return;
      updateFeed(key, (current) => ({
        ...current, events: append ? [...current.events, ...response.data] : response.data,
        cursor: response.meta.next_cursor, hasMore: response.meta.has_more,
        loading: false, loadingMore: false, hasLoadedOnce: true,
        loadedAt: Date.now(), error: null,
      }));
    } catch (reason: unknown) {
      if (id !== sequence.current || currentKey.current !== key) return;
      updateFeed(key, (current) => ({
        ...current, loading: false, loadingMore: false, hasLoadedOnce: true,
        error: reason instanceof SocialApiError && reason.status === 401
          ? 'Войдите, чтобы видеть активности подписок'
          : 'Не удалось загрузить ленту. Попробуйте ещё раз.',
      }));
    }
  }, [authenticated, cacheKey, cityId, tab, updateFeed]);

  useEffect(() => {
    // Switching back paints cached cards immediately; only stale data revalidates.
    sequence.current += 1;
    const cached = cacheRef.current[cacheKey];
    if (!cached?.hasLoadedOnce || Date.now() - cached.loadedAt >= FEED_REVALIDATE_MS) void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  useEffect(() => {
    const onFocus = () => {
      const cached = cacheRef.current[cacheKey];
      if (cached?.hasLoadedOnce && !cached.loading &&
          Date.now() - cached.loadedAt >= FEED_REVALIDATE_MS) void load(false);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [cacheKey, load]);

  const emptyText = tab === 'city'
    ? 'В выбранном городе пока нет новых важных событий.'
    : 'Подпишитесь на людей или заведения, чтобы видеть их активность здесь.';

  const sponsoredPlacement = tab === 'city' && cityId
    ? <SponsoredPlacementCard city={cityId} surface="city_feed_inline" />
    : null;

  const center = (
    <div className="pulse-center">
      <div className="pulse-mobile-page-heading">
        <h1>Пульс города</h1>
      </div>
      <header className="pulse-center__topbar">
        <div className="pulse-center__identity">
          <span className="pulse-eyebrow">{tab === 'city' ? cityName : 'Ваши подписки'}</span>
          <h1>{tab === 'city' ? 'Пульс города' : 'Активность подписок'}</h1>
        </div>
        {cityId && !desktopRankings && <div className="pulse-mobile-rankings-grid" aria-label="Рейтинги города">
          <div className="pulse-segmented pulse-ranking-kind-tabs" role="tablist" aria-label="Тип рейтинга">
            <button type="button" role="tab" aria-selected={mobileRanking === 'venues'} className={mobileRanking === 'venues' ? 'is-active' : ''} onClick={() => setMobileRanking('venues')}>Заведения</button>
            <button type="button" role="tab" aria-selected={mobileRanking === 'guests'} className={mobileRanking === 'guests' ? 'is-active' : ''} onClick={() => setMobileRanking('guests')}>Гости</button>
          </div>
          {mobileRanking === 'guests' ? (
            <CityLeaderboardCard cityId={cityId} limit={5} mobileSwitch onOpenUser={onOpenUser} />
          ) : (
            <VenueRankingCard cityId={cityId} metric="live_score" title="Рейтинг заведений" limit={5} mobileSwitch onOpenVenue={onOpenVenue} />
          )}
        </div>}
        <div className="pulse-segmented pulse-feed-tabs" role="tablist" aria-label="Лента">
          <button type="button" role="tab" aria-selected={tab === 'city'} className={tab === 'city' ? 'is-active' : ''} onClick={() => setTab('city')}>Все</button>
          <button type="button" role="tab" aria-selected={tab === 'following'} className={tab === 'following' ? 'is-active' : ''} onClick={() => setTab('following')}>Подписки</button>
        </div>
      </header>

      {tab === 'city' && !cityId && cityStatePanel}
      {tab === 'city' && cityId && <EveningShowcaseStrip city={cityId} />}

      {tab === 'city' && !cityId ? null : tab === 'following' && !authenticated ? (
        <section className="pulse-empty"><strong>Войдите, чтобы видеть активности подписок</strong><span>Городская лента остаётся доступна без авторизации.</span></section>
      ) : error && events.length === 0 ? (
        <section className="pulse-empty is-error"><strong>{error}</strong><Button variant="outline" size="sm" onClick={() => void load(false)}>Повторить</Button></section>
      ) : !hasLoadedOnce && events.length === 0 && loading ? <FeedSkeleton /> : events.length === 0 ? (
        <>
          <section className="pulse-empty" aria-busy={loading}><HopperIcon name="empty" size={28} /><strong>{emptyText}</strong></section>
          {sponsoredPlacement}
        </>
      ) : (
        <section className="pulse-feed-list" aria-live="polite" aria-busy={loading}>
          {events.slice(0, 3).map((event) => (
            <FeedCard
              key={event.id}
              event={event}
              currentUserId={userContext.userId}
              onOpenUser={onOpenUser}
              onOpenVenue={onOpenVenue}
              onShowVenueOnMap={onShowVenueOnMap}
              contextCityName={tab === 'city' ? cityName : null}
              presentation="pulse"
            />
          ))}
          {sponsoredPlacement}
          {events.slice(3).map((event) => (
            <FeedCard
              key={event.id}
              event={event}
              currentUserId={userContext.userId}
              onOpenUser={onOpenUser}
              onOpenVenue={onOpenVenue}
              onShowVenueOnMap={onShowVenueOnMap}
              contextCityName={tab === 'city' ? cityName : null}
              presentation="pulse"
            />
          ))}
        </section>
      )}

      {loadingMore && events.length > 0 && <div className="pulse-loading">Загружаем ещё…</div>}
      {hasMore && !loading && !loadingMore && <Button variant="outline" className="pulse-load-more" onClick={() => void load(true)}>Показать ещё</Button>}
    </div>
  );

  let body: React.ReactNode;
  if (view === 'visits') {
    body = <PulseVisitsScreen onBack={() => setView('pulse')} onOpenVenue={onOpenVenue} />;
  } else if (view === 'saved') {
    body = <PulseSavedPlacesScreen onBack={() => setView('pulse')} onOpenVenue={onOpenVenue} />;
  } else if (view === 'routes') {
    body = <PulseRoutesSoonScreen onBack={() => setView('pulse')} />;
  } else {
    body = (
      <DesktopThreeColumnLayout
        className="pulse-layout pulse-layout--rebuilt pulse-layout--experience"
        left={cityId && desktopRankings ? (
          <div className="pulse-ranking-rail">
            <CityLeaderboardCard cityId={cityId} onOpenUser={onOpenUser} />
            <VenueRankingCard cityId={cityId} metric="live_score" title="Live Score" onOpenVenue={onOpenVenue} />
            <VenueRankingCard cityId={cityId} metric="hopper_score" title="Hopper Score" onOpenVenue={onOpenVenue} />
          </div>
        ) : null}
        right={(
          <FeedContextSidebar
            userContext={userContext}
            actions={userContextActions}
            cityName={cityName}
          />
        )}
      >
        {center}
      </DesktopThreeColumnLayout>
    );
  }

  return (
    <main className="pulse-rebuild-root">
      {body}

      {view === 'pulse' && (
        <button className="pulse-scan-fab" type="button" aria-label="Сканировать чек" onClick={onScan}>
          <HopperIcon name="scan" size={24} />
        </button>
      )}

      <PulseMobileNav
        active={view === 'routes' ? 'routes' : 'pulse'}
        onOpenPulse={() => setView('pulse')}
        onOpenRoutes={() => setView('routes')}
      />
    </main>
  );
}
